import { apiErrorMessage } from '@agos/api-client'
import { patchOrdersOrderIdItemsItemIdServedStatus } from '@agos/api-client/orders'
import { tt } from '@agos/shared'
import { useToast } from '@agos/ui'
import { useState } from 'react'

/**
 * Striking a line off once it has been carried to the table — `FRONTEND.md` §9.127, the owner's
 * point 12.
 *
 * **One writer, used by two screens.** *Otvorené stoly* and the table map's side panel both do this,
 * and two optimistic writers against one endpoint is how they come to disagree about rollback.
 */

export interface ServableLine {
  id?: number
  servedStatus?: string | null
}

/**
 * A tick that has been made but whose refetch has not landed.
 *
 * <p><b>`wasServer` is what makes this self-healing.</b> Holding only the wanted value — which is
 * what the side panel used to do — is right while the write is in flight and wrong forever after:
 * once somebody else un-serves that line on another device, the refetch brings `pending` and the
 * screen goes on showing it served for the rest of the session. Recording the server value the tick
 * was made against tells the two cases apart without an effect and without a clock.
 */
export interface PendingTick {
  wanted: string
  wasServer: string
}

export const SERVED = 'served'
export const PENDING = 'pending'

function serverStatusOf(line: ServableLine): string {
  return line.servedStatus ?? PENDING
}

/**
 * What a line's state actually is, given what the server says and what is in flight.
 *
 * <p>Pure, so it can be read during render on a list that refetches on a timer — §9.126 already had
 * a lint rule refuse an effect on that page, and rightly.
 */
export function effectiveServed(
  line: ServableLine,
  ticks: Readonly<Record<number, PendingTick>>,
): string {
  const server = serverStatusOf(line)
  const tick = ticks[line.id ?? 0]
  if (tick === undefined) return server
  // The server has moved since the tick was made: somebody else's write landed and it wins.
  if (server !== tick.wasServer) return server
  return tick.wanted
}

/** Whether everything on the bill has reached the table. An empty bill has not. */
export function allServed(
  lines: readonly ServableLine[],
  ticks: Readonly<Record<number, PendingTick>>,
): boolean {
  return lines.length > 0 && lines.every((line) => effectiveServed(line, ticks) === SERVED)
}
/** The status the kitchen moves an order to once it is cooked and waiting to be carried out. */
export const QUALITY_CHECK = 'quality_check'

/**
 * Whether this table has food ready that nobody has carried out - §9.443, FB-768.
 *
 * **The owner:** *"When something is in «Kontrola Kvality» maake sure it will have glowing border…
 * When everything will be «Vyniesť», then do not do that."*
 *
 * **The second sentence is the rule.** *Reached quality check* is not the state worth flashing about
 * - *has something ready that nobody has taken* is. A blink that carries on after the food is on the
 * table trains people to ignore it, which costs more than never having had one.
 *
 * **It reads through the in-flight ticks** because {@link allServed} does: a waiter who has just
 * ticked the last line sees it stop at once rather than after the next eight-second poll. Written
 * here beside that function rather than in the card's JSX, so the two halves of one rule cannot
 * drift apart.
 */
export function isWaitingToBeCarried(
  status: string | null | undefined,
  lines: readonly ServableLine[],
  ticks: Record<number, PendingTick>,
): boolean {
  if (status !== QUALITY_CHECK) return false
  // **Nothing to carry is not something to carry.** `allServed` answers false for an empty list by
  // construction - *nothing is served* - so without this an order that reached quality check and
  // then had its last line removed would blink for ever with no way to stop it, which is precisely
  // the failure the owner's second sentence is about.
  if (lines.length === 0) return false
  return !allServed(lines, ticks)
}


/** The other state — clicking a served line puts it back. */
export function toggledFrom(current: string): string {
  return current === SERVED ? PENDING : SERVED
}

export function useServedLines(onChanged: () => void) {
  const [ticks, setTicks] = useState<Record<number, PendingTick>>({})
  const [isMarkingAll, setIsMarkingAll] = useState(false)
  const { toast } = useToast()

  const setServed = async (orderId: number, line: ServableLine, next: string) => {
    const lineId = line.id ?? 0
    setTicks((current) => ({
      ...current,
      [lineId]: { wanted: next, wasServer: serverStatusOf(line) },
    }))
    try {
      await patchOrdersOrderIdItemsItemIdServedStatus(orderId, lineId, { servedStatus: next })
      onChanged()
    } catch (caught) {
      setTicks((current) => {
        const rolledBack = { ...current }
        delete rolledBack[lineId]
        return rolledBack
      })
      toast({
        variant: 'destructive',
        title: tt('Nepodarilo sa uložiť'),
        description: apiErrorMessage(caught, ''),
      })
    }
  }

  const markAllServed = async (orderId: number, lines: readonly ServableLine[]) => {
    const outstanding = lines.filter((line) => effectiveServed(line, ticks) !== SERVED)
    if (outstanding.length === 0) return

    setIsMarkingAll(true)
    setTicks((current) => {
      const next = { ...current }
      for (const line of outstanding) {
        next[line.id ?? 0] = { wanted: SERVED, wasServer: serverStatusOf(line) }
      }
      return next
    })
    try {
      await Promise.all(
        outstanding.map((line) =>
          patchOrdersOrderIdItemsItemIdServedStatus(orderId, line.id ?? 0, {
            servedStatus: SERVED,
          }),
        ),
      )
      onChanged()
    } catch (caught) {
      // The patches went together, so which of them failed is unknown. Every optimistic tick is
      // dropped and the refetch is left to give the only honest account of what landed.
      setTicks({})
      onChanged()
      toast({
        variant: 'destructive',
        title: tt('Niektoré položky sa neuložili'),
        description: apiErrorMessage(caught, ''),
      })
    } finally {
      setIsMarkingAll(false)
    }
  }

  return { ticks, isMarkingAll, setServed, markAllServed }
}
